import { buildType } from "@helpers/app-identity";
import { isWeb } from "@memorang/helpers/platform";
import { deleteKey, retrieveKey, storeKey } from "@memorang/helpers/storage";
/* -----------------Helpers & Hooks--------------- */
import { useNavigationContainerRef } from "expo-router";
/* -----------------Globals--------------- */
import { useEffect, useState } from "react";

/* -----------------Child components--------------- */
import * as Font from "expo-font";
import * as SplashScreen from "expo-splash-screen";

import { IS_DEV } from "@constants/env";
import { parseAppConfig } from "@helpers/app-config";
import { isAxiosError, publicHubAxios } from "@helpers/lib/axios";
import { reactNavigationIntegration } from "@helpers/lib/sentry";
import { captureErrorToSentry } from "@helpers/sentry-logger";
import { HUB_ENDPOINTS } from "@memorang/constants/urls";
import { customFonts } from "../../assets";
import {
	type StoredThemePreferenceResolution,
	resolveStoredThemePreference,
} from "../contexts/theme-preference";
import { setupAudioPlayer } from "../libs/audio-player";
/* -----------------Others--------------- */
import { initializeOneSignal } from "../libs/onesignal";
import {
	isTransientAppConfigFetchError,
	serializeAppConfigFetchError,
} from "./app-bootstrap-helpers";
import { useAppStore } from "./useAppStore";

const normalizeAppConfigError = (error: unknown) => {
	if (!(isAxiosError(error) && error.response)) {
		return error;
	}

	return Object.assign(
		new Error(
			`App config request error for build type "${buildType}": HTTP ${error.response.status} ${error.message}`,
		),
		{ code: String(error.response.status) },
	);
};

export default function useAppBootstrap() {
	const [isLoadingComplete, setLoadingComplete] = useState(false);

	const [themeResolution] = useState<StoredThemePreferenceResolution>(() =>
		resolveStoredThemePreference({
			storedPreference: retrieveKey("themePreference"),
			legacyIsDarkMode: retrieveKey("isDarkMode"),
		}),
	);

	useEffect(() => {
		if (themeResolution.needsPersistence) {
			storeKey("themePreference", themeResolution.preference);
			deleteKey("isDarkMode");
		}
	}, [themeResolution]);

	const oneSignalAppId = useAppStore((store) => store.app.keys.oneSignalAppId);
	const setAppConfig = useAppStore((store) => store.setAppConfig);

	const ref = useNavigationContainerRef();

	useEffect(() => {
		setupAudioPlayer().catch((error) => {
			console.warn("[audio-player] Failed to initialize", error);
		});
	}, []);

	useEffect(() => {
		if (ref && !isWeb) {
			reactNavigationIntegration.registerNavigationContainer(ref);
		}
	}, [ref]);

	useEffect(() => {
		async function loadResourcesAndDataAsync() {
			try {
				SplashScreen.preventAutoHideAsync();
				// Load fonts
				await Font.loadAsync(customFonts);
				initializeOneSignal(oneSignalAppId);
			} catch (e) {
				console.warn(e);
			} finally {
				setLoadingComplete(true);
				SplashScreen.hideAsync();
			}
		}

		loadResourcesAndDataAsync();
	}, [oneSignalAppId]);

	useEffect(() => {
		(async () => {
			try {
				if (IS_DEV) {
					return;
				}
				const { data } = await publicHubAxios.get(
					HUB_ENDPOINTS.APP_CONFIG(buildType),
				);
				const parsedConfig = parseAppConfig(data);

				if (!parsedConfig) {
					throw new Error(`No app config found for build type "${buildType}"`);
				}
				setAppConfig(parsedConfig);
			} catch (error) {
				const appConfigError = normalizeAppConfigError(error);

				if (isTransientAppConfigFetchError(appConfigError)) {
					console.warn(
						"[app-config] Failed to fetch remote app config, using bundled defaults",
						appConfigError,
					);
					return;
				}

				console.error("❌ Error fetching custom app config:", appConfigError);
				const errorObj = new Error("Failed to fetch app config");
				captureErrorToSentry(errorObj, {
					buildType,
					...serializeAppConfigFetchError(appConfigError),
				});
			} finally {
				// setIsLoadingConfig(false);
			}
		})();
	}, [setAppConfig]);

	return {
		isLoadingComplete,
		themePreference: themeResolution.preference,
	};
}
